import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import {
  FaCalendarAlt,
  FaDownload,
  FaEnvelope,
  FaTimes,
  FaUserMd,
  FaClock,
  FaHospital,
  FaCheckCircle,
  FaCalendarPlus,
} from "react-icons/fa";

import axios from "../../api/axios";
import BottomNav from "../../components/BottomNav";
import HeaderWithBack from "../../components/HeaderWithBack";
import SendEmail from "../../components/modal/SendEmail";
import Loading from "../../components/Loading";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "../../styles/styles.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

const ReferenceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { referenceId } = useParams<{ referenceId: string }>();
  const storedData = sessionStorage.getItem("userData");
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;
  const email = userData ? userData.userData : null;

  const [referenceData, setReferenceData] = useState<any>(null);
  const [showPdf, setShowPdf] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`reference/show/${referenceId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setReferenceData(response.data.data);
      } catch (error) {
        console.error("Error fetching reference data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token, referenceId]);

  // Check expired
  let isExpired = false;
  if (referenceData?.reference_expired_date) {
    const [day, month, year] = referenceData.reference_expired_date.split("-");
    const expiredDate = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiredDate.setHours(0, 0, 0, 0);
    isExpired = expiredDate < today;
  }

  const canBook =
    !referenceData?.is_booked &&
    !isExpired &&
    ["active", "approved", "final"].includes(
      referenceData?.status?.toLowerCase(),
    );

  const bookingData = referenceData?.booking_data;

  const publicPdfUrl = referenceData?.pdf_url;

  const handleDownloadPdf = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `reference/download/${referenceId}`,
        { hospital_id: referenceData?.hospital_id },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" },
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rujukan_${referenceData?.reference_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      alert("Gagal mengunduh PDF");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await axios.get(`brilife/reference/send-email/${referenceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Berhasil mengirim rujukan ke email");
    } catch {
      alert("Gagal mengirim rujukan");
    } finally {
      setIsLoading(false);
      setShowModal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      active: { color: "bg-green-500", text: "Aktif" },
      approved: { color: "bg-green-500", text: "Disetujui" },
      final: { color: "bg-blue-500", text: "Final" },
      pending: { color: "bg-yellow-500", text: "Menunggu" },
      expired: { color: "bg-red-500", text: "Kadaluarsa" },
      rejected: { color: "bg-red-500", text: "Ditolak" },
      draft: { color: "bg-gray-400", text: "Draft" },
    };
    const config = statusConfig[status?.toLowerCase()] || {
      color: "bg-gray-500",
      text: status,
    };
    return (
      <span
        className={`${config.color} text-white px-3 py-1 rounded-full text-xs font-semibold`}
      >
        {config.text}
      </span>
    );
  };

  const formatBookingDate = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const formatBookedAt = (dateStr: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading && !referenceData) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <HeaderWithBack title="Detail Rujukan" />
        <div className="flex-1 flex items-center justify-center">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pb-20">
      {/* Header */}
      <HeaderWithBack title="Detail Rujukan" />

      {/* Status bar */}
      <div className="bg-teal-500 text-white px-4 py-2 flex items-center justify-between">
        <div className="flex items-center">
          <FaCalendarAlt className="mr-2" />
          <span className="text-sm font-medium">Rujukan Anda</span>
        </div>
        {referenceData && getStatusBadge(referenceData.status)}
      </div>

      {/* Expired warning */}
      {referenceData?.reference_expired_date &&
        (isExpired ? (
          <div className="bg-red-500 text-white px-4 py-2 text-sm text-center">
            Rujukan Sudah Kadaluarsa &nbsp;•&nbsp;{" "}
            <strong>{referenceData.reference_expired_date}</strong>
          </div>
        ) : (
          <div className="bg-yellow-100 text-yellow-800 px-4 py-2 text-sm text-center">
            Berlaku sampai{" "}
            <strong>{referenceData.reference_expired_date}</strong>
          </div>
        ))}

      <div className="px-4 mt-4 space-y-4">
        {/* ── Informasi Booking ─────────────────────────────────────────────── */}
        {bookingData ? (
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
            {/* Header booking */}
            <div className="bg-teal-50 border-b border-teal-100 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-teal-500" size={15} />
                <h2 className="font-semibold text-gray-800 text-sm">
                  Informasi Booking
                </h2>
              </div>
              <span className="text-xs bg-teal-500 text-white px-2 py-0.5 rounded-full font-medium">
                {bookingData.status === "success"
                  ? "Terkonfirmasi"
                  : bookingData.status}
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* Kode booking */}
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
                <p className="text-xs text-gray-500 mb-1">Kode Booking</p>
                <p className="text-2xl font-bold text-teal-600 tracking-widest">
                  {bookingData.booking_code}
                </p>
              </div>

              {/* Dokter */}
              <div className="flex items-start gap-3 pb-3 border-b">
                <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                  <FaUserMd className="text-teal-400" size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Dokter</p>
                  <p className="font-semibold text-sm">
                    {bookingData.doctor_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {bookingData.poly_name}
                  </p>
                </div>
              </div>

              {/* Jadwal */}
              <div className="flex items-start gap-3 pb-3 border-b">
                <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center shrink-0">
                  <FaCalendarAlt className="text-teal-400" size={14} />
                </div>
                <div>
                  <p className="text-xs text-gray-400">Jadwal</p>
                  <p className="font-semibold text-sm">
                    {formatBookingDate(bookingData.booking_date)}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <FaClock className="text-gray-300" size={11} />
                    <p className="text-xs text-gray-500">
                      {bookingData.booking_time} {bookingData.timezone}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dipesan pada */}
              <div className="text-xs text-gray-400 text-right">
                Dipesan pada {formatBookedAt(bookingData.booked_at)}
              </div>
            </div>
          </div>
        ) : (
          /* Belum ada booking */
          !isExpired &&
          canBook && (
            <div className="bg-white border border-dashed border-teal-300 rounded-xl p-5 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center">
                <FaCalendarPlus className="text-teal-400" size={20} />
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-sm">
                  Belum Ada Booking
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Lakukan booking untuk membuat jadwal kunjungan ke rumah sakit
                </p>
              </div>
              <button
                onClick={() =>
                  navigate(`/reference/${referenceId}/booking/doctors`, {
                    state: {
                      providerCode: referenceData?.provider_code,
                      specialityCode: referenceData?.speciality_code,
                      specialityName: referenceData?.speciality_name,
                      polyCode: referenceData?.poly_code,
                      polyName: referenceData?.poly_name || referenceData?.poly,
                    },
                  })
                }
                className="bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors flex items-center gap-2"
              >
                <FaCalendarPlus size={13} />
                Buat Booking
              </button>
            </div>
          )
        )}

        {/* ── Informasi Rujukan ─────────────────────────────────────────────── */}
        <div className="bg-white border rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-800 mb-3">
            Informasi Rujukan
          </h2>

          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-400 mb-1">Nomor Rujukan</p>
            <p className="font-bold text-base">
              {referenceData?.reference_number}
            </p>
          </div>

          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-400 mb-1">Rumah Sakit Tujuan</p>
            <p className="font-semibold">{referenceData?.provider_name}</p>
            {referenceData?.provider_code && (
              <p className="text-xs text-gray-500 mt-0.5">
                Kode: {referenceData.provider_code}
              </p>
            )}
          </div>

          <div className="mb-3 pb-3 border-b">
            <p className="text-xs text-gray-400 mb-1">{referenceData?.speciality_name ? 'Spesialisasi' : 'Poliklinik'}</p>
            <p className="font-semibold">
              {referenceData?.speciality_name || referenceData?.poly_name || referenceData?.poly}
            </p>
            {(referenceData?.speciality_code || referenceData?.poly_code) && (
              <p className="text-xs text-gray-500 mt-0.5">
                Kode: {referenceData.speciality_code || referenceData.poly_code}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-1">Tipe Rujukan</p>
              <p className="font-semibold capitalize">{referenceData?.type}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">Tanggal Rujukan</p>
              <p className="font-semibold">{referenceData?.reference_date}</p>
            </div>
          </div>

          {referenceData?.doctor_note && (
            <div className="mt-3 bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-400 mb-1">Catatan Dokter</p>
              <p className="text-sm text-gray-700">
                {referenceData.doctor_note}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <Loading />
        </div>
      )}

      {/* Send Email Modal */}
      <SendEmail
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirm}
        email={email}
      />

      <BottomNav />
    </div>
  );
};

export default ReferenceDetail;
